import { Injectable, inject } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { MessageService } from '@openng/optimus-ui/api';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';

export interface NavMessage {
  headerKey: string;
  headerParams?: any;
  bodyKey: string;
  bodyParams?: any;
}

/**
 * There are two message components in the UI.
 *
 * The toast component is used for temporary messages and the alert component is used for messages that require the user
 * to acknowledge them.
 *
 * INFO - toast
 * SUCCESS - toast
 * WARNING - alert
 * ERROR - alert
 */

@Injectable({
  providedIn: 'root',
})
export class LogAndMessageService {
  private _logger = inject(NGXLogger);
  private _messageService = inject(MessageService);
  private _translateService = inject(TranslateService);

  TIMEOUT = 5000;

  errorLogOnly(body: string): void {
    this._logger.error(body);
  }

  error(header: string, body: string): void {
    this.errorAlert(`${header} - ${body}`);
  }

  errorAlert(body: string): void {
    alert(body);
    this._logger.error(body);
  }

  warn(body: string): void {
    this.warnLogOnly(body);
    this.warnAlert(body);
  }

  warnLogOnly(body: string): void {
    this._logger.warn(body);
  }

  warnAlert(body: string): void {
    alert(body);
    this._logger.warn(body);
  }

  info(header: string, body: string): void {
    this.infoLogOnly(body);
    this.infoMessageOnly(header, body);
  }

  infoLogOnly(body: string): void {
    this._logger.info(body);
  }

  infoMessageOnly(header: string, body: string): void {
    let key = 'toast';
    this._messageService.add({
      severity: 'info',
      summary: header,
      detail: body,
      key: key,
      life: 7000,
    });
  }

  success(header: string, body: string): void {
    this.infoLogOnly(body);
    this.successMessageOnly(header, body);
  }

  successMessageOnly(header: string, body: string): void {
    let key = 'toast';

    this._messageService.add({
      severity: 'success',
      summary: header,
      detail: body,
      key: key,
      life: 5000,
    });
  }

  debug(body: string): void {
    this._logger.debug(body);
  }

  translateToErrorAlert(message: NavMessage) {
    const header$ = this._translateService.get(
      message.headerKey,
      message.headerParams
    );
    const msg$ = this._translateService.get(
      message.bodyKey,
      message.bodyParams
    );
    forkJoin([header$, msg$]).subscribe((messages: string[]) => {
      this.errorAlert(`${messages[0]} - ${messages[1]}`);
    });
  }

  translateToWarnMessage(message: NavMessage) {
    const header$ = this._translateService.get(
      message.headerKey,
      message.headerParams
    );
    const msg$ = this._translateService.get(
      message.bodyKey,
      message.bodyParams
    );
    forkJoin([header$, msg$]).subscribe((messages: string[]) => {
      this.warn(`${messages[0]} - ${messages[1]}`);
    });
  }

  translateToInfoMessage(message: NavMessage) {
    const header$ = this._translateService.get(
      message.headerKey,
      message.headerParams
    );
    const msg$ = this._translateService.get(
      message.bodyKey,
      message.bodyParams
    );
    forkJoin([header$, msg$]).subscribe((messages: string[]) => {
      this.info(messages[0], messages[1]);
    });
  }

  translateToSuccessMessage(message: NavMessage) {
    const header$ = this._translateService.get(
      message.headerKey,
      message.headerParams
    );
    const msg$ = this._translateService.get(
      message.bodyKey,
      message.bodyParams
    );
    forkJoin([header$, msg$]).subscribe((messages: string[]) => {
      this.success(messages[0], messages[1]);
    });
  }

  clear() {
    this._messageService.clear();
  }
}
